/**
 * useSubscriptionStatus Hook
 * 
 * Responsabilidade: Derivar estados e informações de status da assinatura.
 * Calcula valores derivados como isBlocked, trialDaysLeft, statusText.
 * 
 * Princípios:
 * - Single Responsibility: Apenas lógica de status/estado derivado
 * - DRY: Centraliza cálculos de status em um só lugar
 */

import { useMemo } from 'react';
import type { OrganizationSubscription } from '../../../domain/entities/Subscription';
import { isSubscriptionBlocked, getTrialDaysRemaining } from '../../../domain/entities/Subscription';

// =============================================================================
// TIPOS
// =============================================================================

export interface SubscriptionStatusInfo {
    isBlocked: boolean;
    trialDaysLeft: number;
    statusText: string;
    canUpgrade: boolean;
    isPastDue: boolean;
    isTrialing: boolean;
    isActive: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export const useSubscriptionStatus = (
    subscription: OrganizationSubscription | null
): SubscriptionStatusInfo => {

    return useMemo(() => {
        if (!subscription) {
            return {
                isBlocked: false,
                trialDaysLeft: 0,
                statusText: 'Carregando...',
                canUpgrade: false,
                isPastDue: false,
                isTrialing: false,
                isActive: false,
            };
        }

        const isBlocked = isSubscriptionBlocked(subscription);
        const trialDaysLeft = subscription.trialEndsAt
            ? getTrialDaysRemaining(subscription.trialEndsAt)
            : 0;

        // Determinar status text
        let statusText: string;
        switch (subscription.status) {
            case 'trialing':
                statusText = `Trial (${trialDaysLeft} dias restantes)`;
                break;
            case 'active':
                statusText = 'Ativo';
                break;
            case 'past_due':
                statusText = 'Pagamento pendente';
                break;
            case 'canceled':
                statusText = 'Cancelado';
                break;
            case 'blocked':
                statusText = 'Bloqueado';
                break;
            default:
                statusText = subscription.status;
        }

        return {
            isBlocked,
            trialDaysLeft,
            statusText,
            canUpgrade: !isBlocked && subscription.status !== 'canceled',
            isPastDue: subscription.status === 'past_due',
            isTrialing: subscription.status === 'trialing',
            isActive: subscription.status === 'active',
        };
    }, [subscription]);
};
